import { 
  signInWithEmailAndPassword,
  signOut,
  getIdToken,
  signInWithPopup
} from 'firebase/auth'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import adminApi from '../services/adminApi'
import api from '../services/api'
import { auth, googleProvider, facebookProvider, isFirebaseEnabled, onAuthStateChanged } from '../services/firebase'

import { setToken } from '../services/tokenManager'
import { normalizeRole, roleIsOneOf } from '../utils/roles'

export const useUserStore = defineStore('user', () => {
  const user = ref(null)
  const loading = ref(false)
  const initialized = ref(false)
  const error = ref(null)
  const connectedProviders = ref([])
  const consentStatus = ref(null)
  const firebaseUser = ref(null)
  const idToken = ref(localStorage.getItem('token') || null)
  let resolveAuthReady
  let authReadyResolved = false
  const authReady = new Promise((resolve) => {
    resolveAuthReady = resolve
  })

  const adminAccessToken = ref(localStorage.getItem('adminAccessToken') || '')
  const adminRefreshToken = ref(localStorage.getItem('adminRefreshToken') || '')
  const adminUser = ref(JSON.parse(localStorage.getItem('adminUser') || 'null'))

  const isAuthenticated = computed(() => !!user.value)
  const canUseFirebaseAuth = computed(() => isFirebaseEnabled)
  const userRole = computed(() => normalizeRole(user.value?.role))
  const onboardingComplete = computed(() => {
    if (consentStatus.value?.onboardingComplete === true) return true
    if (user.value?.onboardingComplete === true) return true
    return false
  })
  const hasRole = (...roles) => roleIsOneOf(user.value?.role, roles)
  const adminScope = computed(() => adminUser.value?.adminScope || null)
  const isAdminAuthenticated = computed(() => !!adminAccessToken.value && !!adminScope.value)

  function markAuthReady() {
    if (!authReadyResolved) {
      authReadyResolved = true
      resolveAuthReady()
    }
  }

  const isFirebaseToken = (t) => t && t.length > 100 && t.includes('.')

  const refreshFirebaseToken = async (forceRefresh = false) => {
    if (!isFirebaseEnabled || !auth?.currentUser) {
      // Only clear if it's currently a Firebase token
      if (isFirebaseToken(idToken.value)) {
        idToken.value = null
        setToken(null)
      }
      return null
    }

    firebaseUser.value = auth.currentUser
    idToken.value = await getIdToken(auth.currentUser, forceRefresh)
    setToken(idToken.value)
    return idToken.value
  }

  const getAuthToken = async ({ forceRefresh = false } = {}) => {
    // If we already have a token and it's local (hex), or not forcing refresh, return it
    if (idToken.value && !forceRefresh) {
      // If it's a local token, just return it
      if (!isFirebaseToken(idToken.value)) return idToken.value
      
      // If it's a Firebase token, we could check expiration, but for now just return
      return idToken.value
    }
    
    // Otherwise try to refresh from Firebase
    return await refreshFirebaseToken(forceRefresh)
  }

  const clearSession = () => {
    user.value = null
    firebaseUser.value = null
    idToken.value = null
    setToken(null)
    connectedProviders.value = []
    consentStatus.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('sessionToken')
    localStorage.removeItem('user')
  }

  const clearAdminSession = () => {
    adminAccessToken.value = ''
    adminRefreshToken.value = ''
    adminUser.value = null
    localStorage.removeItem('adminAccessToken')
    localStorage.removeItem('adminRefreshToken')
    localStorage.removeItem('adminUser')
  }

  let profilePromise = null

  const getProfile = async ({ silent = false } = {}) => {
    // Prevent multiple concurrent profile fetches
    if (profilePromise) return profilePromise
    
    profilePromise = (async () => {
      try {
        const config = { skipAuthRedirect: silent }
        // Explicitly attach token if available, otherwise reliance on cookies
        const token = await getAuthToken()
        if (token) {
          config.headers = { Authorization: `Bearer ${token}` }
        }
        
        const response = await api.get('/auth/me', config)
        user.value = response.data.user
        connectedProviders.value = response.data.user.providers || []
        consentStatus.value = response.data.consentStatus
        error.value = null
        return response.data.user
      } catch (err) {
        if (!silent) {
          error.value = err.response?.data?.error || 'Failed to load your authenticated session.'
        }
        return null
      } finally {
        profilePromise = null
        initialized.value = true
      }
    })()
    
    return profilePromise
  }

  // Initialize Firebase Auth Listener
  onAuthStateChanged(auth, async (fUser) => {
    try {
      if (fUser) {
        console.log('[UserStore] Firebase user detected:', fUser.uid)
        firebaseUser.value = fUser
        await refreshFirebaseToken()
        await getProfile({ silent: true })
      } else {
        console.log('[UserStore] No Firebase user')
        firebaseUser.value = null
        // Do NOT clear idToken or call setToken(null) here if we have a local session.
        // Local session tokens are 32-byte hex strings, Firebase tokens are long JWTs.
        const hadFirebaseToken = isFirebaseToken(idToken.value)
        if (hadFirebaseToken) {
          idToken.value = null
          setToken(null)
        }
        
        // Only clear user if not logged in via local session
        if (!user.value && hadFirebaseToken) {
          clearSession()
        }
      }
    } catch (err) {
      console.warn('[UserStore] Firebase session restore failed:', err.message)
    } finally {
      markAuthReady()
    }
  })

  const restoreSession = async () => {
    // Wait for Firebase to restore persisted auth before the router decides.
    await authReady
    if (user.value) return user.value
    const token = await getAuthToken()
    if (!token) {
      initialized.value = true
      return null
    }
    return await getProfile({ silent: true })
  }

  const login = async (email, password) => {
    loading.value = true
    error.value = null
    try {
    // 1. Try Local Backend Login if enabled (preferred for dev/test)
    const isLocalAuthEnabled = import.meta.env.VITE_ENABLE_LOCAL_AUTH !== 'false'
    if (isLocalAuthEnabled) {
      try {
        console.log(`[UserStore] Attempting local backend login for ${email}...`)
        // A cold Cloud Run /auth/login can take ~8-10s to respond; 10s cut real logins off.
        const response = await api.post('/auth/login', { email, password }, { timeout: 20000 })
        if (response.data.success) {
          if (response.data.token) {
            idToken.value = response.data.token
            setToken(idToken.value)
          }
          user.value = response.data.user
          initialized.value = true
          // The login payload carries NO consent state; without this fetch every
          // consent-gated route reads "missing" and re-prompts on each login.
          // Non-fatal on failure — the router guard refetches before deciding.
          try { await getConsentStatus() } catch { /* guard refetches */ }
          return user.value
        }
      } catch (localErr) {
        console.warn('[UserStore] Local backend login failed:', localErr.response?.data?.error || localErr.message)
        error.value = localErr.code === 'ECONNABORTED'
          ? 'The server took too long to respond. Please try again.'
          : (localErr.response?.data?.error || 'Invalid email or password.')
        return null
      }
    }

    // 2. Try Firebase Login
    if (!isFirebaseEnabled) {
      error.value = 'Firebase sign-in is not configured for this deployment.'
      return null
    }

    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      firebaseUser.value = result.user
      await refreshFirebaseToken(true)
      const profile = await getProfile()
      if (!profile) {
        await signOut(auth)
        return null
      }
      return profile
    } catch (err) {
      console.error('[UserStore] Firebase login error:', err.code, err.message)
      error.value = getFirebaseErrorMessage(err.code)
      return null
    }
    } finally {
      // Every return path above must release the button ("Signing in…" lockup otherwise).
      loading.value = false
    }
  }

  const register = async ({ firstName, lastName, email, password, role }) => {
    loading.value = true
    error.value = null
    try {
      console.log(`[UserStore] Registering ${email} as ${role}...`)
      const response = await api.post(
        '/auth/register',
        { firstName, lastName, email, password, role },
        { timeout: 30000, skipAuthRedirect: true }
      )
      if (response.data.success) {
        if (response.data.token) {
          idToken.value = response.data.token
          setToken(idToken.value)
        }
        user.value = response.data.user
        initialized.value = true
        return user.value
      }
      error.value = response.data.error || 'Registration failed.'
      return null
    } catch (err) {
      console.warn('[UserStore] Registration failed:', err.response?.data?.error || err.message)
      error.value = err.response?.data?.error || 'Registration failed. Please try again.'
      return null
    } finally {
      loading.value = false
    }
  }

  const socialLogin = async (providerName) => {
    loading.value = true
    error.value = null
    if (!isFirebaseEnabled) {
      error.value = 'Social sign-in is not configured for this deployment.'
      loading.value = false
      return null
    }

    try {
      const provider = providerName === 'google' ? googleProvider : facebookProvider
      const result = await signInWithPopup(auth, provider)
      firebaseUser.value = result.user
      await refreshFirebaseToken(true)
      const profile = await getProfile()
      if (!profile) {
        await signOut(auth)
        return null
      }
      return profile
    } catch (err) {
      console.error(`[UserStore] Social login error (${providerName}):`, err.code)
      if (providerName === 'facebook' &&
          (err.code === 'auth/operation-not-allowed' || err.code === 'auth/configuration-not-found')) {
        // Facebook is initialized client-side but not yet enabled in the
        // Firebase project — tell the user it's coming instead of failing
        // with a generic auth error.
        console.warn('[UserStore] Facebook sign-in requested but the provider is not enabled in Firebase yet.')
        error.value = 'Facebook sign-in is coming soon. Please continue with Google or email for now.'
      } else if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // The user dismissed the popup — not an error state.
        error.value = null
      } else {
        error.value = getFirebaseErrorMessage(err.code)
      }
      return null
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    try {
      if (isFirebaseEnabled) {
        await signOut(auth)
      }
      await api.post('/auth/logout')
    } catch (err) {
      console.warn('[UserStore] Logout error:', err.message)
    } finally {
      clearSession()
      loading.value = false
    }
  }

  function getFirebaseErrorMessage(code) {
    switch (code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Invalid email or password.'
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.'
      case 'auth/user-disabled':
        return 'This account has been disabled.'
      default:
        return 'Authentication failed. Please try again.'
    }
  }

  const adminLogin = async (email, password) => {
    loading.value = true
    error.value = null
    try {
      const response = await adminApi.post('/auth/login', { email, password }, { skipAdminRedirect: true })
      adminAccessToken.value = response.data.accessToken
      adminRefreshToken.value = response.data.refreshToken
      adminUser.value = response.data.user
      localStorage.setItem('adminAccessToken', adminAccessToken.value)
      localStorage.setItem('adminRefreshToken', adminRefreshToken.value)
      localStorage.setItem('adminUser', JSON.stringify(adminUser.value))
      return adminUser.value
    } catch (err) {
      clearAdminSession()
      error.value = err.response?.data?.error || 'Admin sign-in failed.'
      return null
    } finally {
      loading.value = false
    }
  }

  const adminLogout = () => {
    clearAdminSession()
  }

  const getConsentStatus = async () => {
    const response = await api.get('/auth/consents')
    consentStatus.value = response.data.consentStatus
    return consentStatus.value
  }

  const hasConsent = (key) => {
    if (!consentStatus.value?.consents) return false
    return consentStatus.value.consents.some(c => c.key === key && c.decision === 'granted')
  }

  const hasConsents = (...keys) => keys.every(key => hasConsent(key))

  const completeConsentOnboarding = async (consents) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.put('/auth/consents/onboarding', { consents })
      consentStatus.value = response.data.consentStatus
      if (user.value) user.value.onboardingComplete = consentStatus.value.onboardingComplete
      return consentStatus.value
    } catch (err) {
      error.value = err.response?.data?.error || 'Unable to record your consent choices.'
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Update a single consent (granted | declined | revoked) via the audited
   * self-service endpoint. Used by the "Privacy & data" settings section and
   * the one-tap consent-unlock flow. Server model unchanged (DSAR/audit intact).
   */
  const setConsent = async (key, decision) => {
    error.value = null
    try {
      const response = await api.patch(`/auth/consents/${key}`, { decision })
      consentStatus.value = response.data.consentStatus
      if (user.value) user.value.onboardingComplete = consentStatus.value.onboardingComplete
      return consentStatus.value
    } catch (err) {
      error.value = err.response?.data?.error || 'Unable to update consent.'
      return null
    }
  }

  /** Grant a list of consent keys one-tap style (consent-unlock flow). */
  const grantConsents = async (keys) => {
    loading.value = true
    try {
      for (const key of keys) {
        const updated = await setConsent(key, 'granted')
        if (!updated) return null
      }
      return consentStatus.value
    } finally {
      loading.value = false
    }
  }

  const disconnectProvider = async (provider) => {
    try {
      await api.delete(`/auth/providers/${provider}`)
      connectedProviders.value = connectedProviders.value.filter(p => p.provider !== provider)
      return true
    } catch (err) {
      error.value = `Failed to disconnect ${provider}`
      return false
    }
  }

  return {
    user,
    loading,
    initialized,
    error,
    connectedProviders,
    consentStatus,
    firebaseUser,
    idToken,
    adminAccessToken,
    adminRefreshToken,
    adminUser,
    isAuthenticated,
    canUseFirebaseAuth,
    userRole,
    onboardingComplete,
    adminScope,
    isAdminAuthenticated,
    hasRole,
    hasConsent,
    hasConsents,
    clearSession,
    clearAdminSession,
    getAuthToken,
    getProfile,
    restoreSession,
    login,
    register,
    socialLogin,
    logout,
    adminLogin,
    adminLogout,
    getConsentStatus,
    completeConsentOnboarding,
    setConsent,
    grantConsents,
    disconnectProvider
  }
})
